import React, { useEffect, useState } from "react";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { InputFormField } from "components/InputFormField";
import { InputColorPicker } from "components/InputColorPicker";
import { useHistory } from "react-router-dom";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { useAlert } from "hooks/useAlert";
import { iconWhiteTrash } from "assets/icons";
import { useTrackForm } from "./useTrackForm";
import { Container, FormSection, Wrapper, FormActions } from "./styles";

interface TrackFormProps {
    eventId?: string;
    trackId?: string;
}

export const TrackForm: React.FC<TrackFormProps> = ({
    eventId,
    trackId
}) => {
    const { push } = useHistory();
    const { connectGraphQLApi } = useGraphQLAuth();
    const { setToast } = useToast();
    const { setTextAlertInterface, openAlert, closeAlert, alert } = useAlert();
    const [editingTrack, setEditingTrack] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const onSuccess = () => {
        push(`/events/${eventId}?tab=Trilhas`);
    };

    const {
        handleInputChange,
        inputsValues,
        errors,
        handleSubmit,
        isLoading,
        isEditing,
    } = useTrackForm(onSuccess, editingTrack, eventId);

    const getErrorMessage = (error: any): string | undefined => {
        if (typeof error === 'string') return error;
        if (Array.isArray(error)) return error[0];
        return undefined;
    };

    // Load track data for editing
    useEffect(() => {
        if (trackId) {
            loadTrackData();
        }
    }, [trackId]);

    // Handle alert confirmation for delete
    useEffect(() => {
        if (alert.typeContinue === "save") {
            executeDeleteTrack();
        }
    }, [alert]);

    const loadTrackData = async () => {
        if (!trackId) return;

        setLoading(true);

        try {
            const query = `
                query BackofficeTrack($trackId: ID!) {
                    backofficeTrack(trackId: $trackId) {
                        trackId
                        eventId
                        name
                        description
                        color
                        colorText
                        insertDate
                        updateDate
                    }
                }
            `;

            const response = await connectGraphQLApi({
                query,
                variables: { trackId },
                errorMessage: 'Erro ao carregar dados da track',
                useNewApi: true,
            });

            const trackData = response.data.backofficeTrack;
            setEditingTrack(trackData);
        } catch (error) {
            console.error('Error loading track data:', error);
            setToast({
                type: "error",
                message: "Erro ao carregar dados da track.",
                isActive: true,
                timeToRemoveToast: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTrack = () => {
        setTextAlertInterface({
            title: "Excluir Track",
            description: "Tem certeza que deseja excluir esta track? Esta ação não pode ser desfeita.",
            closeAlertText: "Cancelar",
            continueAlertText: "Excluir",
            closeAlertTypeBtn: "outline",
            continueAlertTypeBtn: "danger",
            positionCenter: true,
            actionNeeded: false,
        });
        openAlert(true, 'deleteTrack');
    };

    const executeDeleteTrack = async () => {
        if (!trackId) return;

        try {
            const mutation = `
                mutation BackofficeDeleteTrack($trackId: ID!) {
                    backofficeDeleteTrack(trackId: $trackId)
                }
            `;

            await connectGraphQLApi({
                query: mutation,
                variables: { trackId },
                errorMessage: 'Erro ao excluir track',
                useNewApi: true,
            });

            setToast({
                type: "success",
                message: "Track excluída com sucesso!",
                isActive: true,
                timeToRemoveToast: 3000,
            });

            onSuccess();
        } catch (error) {
            setToast({
                type: "error",
                message: "Erro ao excluir track.",
                isActive: true,
                timeToRemoveToast: 5000,
            });
            console.error(error);
        } finally {
            closeAlert();
        }
    };

    if (loading) {
        return (
            <Container>
                <div>Carregando...</div>
            </Container>
        );
    }

    return (
        <Container>
            <Card title="Trilha">
                <FormSection>
                    <Wrapper>
                        <InputFormField
                            label="Nome da trilha"
                            name="name"
                            value={inputsValues.name}
                            onChange={handleInputChange}
                            error={!!errors.name}
                            mensageError={getErrorMessage(errors.name)}
                            required
                            block
                        />

                        <InputFormField
                            label="Descrição"
                            name="description"
                            value={inputsValues.description}
                            onChange={handleInputChange}
                            error={!!errors.description}
                            mensageError={getErrorMessage(errors.description)}
                            block
                        />

                        <InputColorPicker
                            label="Cor da Track"
                            name="color"
                            value={inputsValues.color}
                            onChange={handleInputChange}
                            error={!!errors.color}
                            errorMessage={getErrorMessage(errors.color)}
                            block
                        />

                        <InputColorPicker
                            label="Cor do Texto"
                            name="colorText"
                            value={inputsValues.colorText}
                            onChange={handleInputChange}
                            error={!!errors.colorText}         
                            errorMessage={getErrorMessage(errors.colorText)}
                            block
                        />
                    </Wrapper>
                </FormSection>

                <FormActions>
                    {isEditing ? (
                        <Button
                            label="Excluir Trilha"
                            typeBtn="danger"
                            icon={iconWhiteTrash}
                            onClick={handleDeleteTrack}
                            block={false}
                        />
                    ) : (
                        <div />
                    )}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button
                            label="Cancelar"
                            typeBtn="outline"
                            onClick={() => push(`/events/${eventId}?tab=Trilhas`)}
                            block={false}
                        />

                        <Button
                            label={isEditing ? "Salvar" : "Criar Track"}
                            typeBtn="blue"
                            onClick={handleSubmit}
                            disable={isLoading}
                            load={isLoading}
                            block={false}
                        />
                    </div>
                </FormActions>
            </Card>
        </Container>
    );
}; 